/**
 * API repository - Simple HTTP data access layer
 * No business logic - just GET/POST operations
 *
 * Server always returns ContentUnit format: { text, styles }
 */

import type {
  NetworkRepositoryConfig,
  ProjectConfig,
} from '../config/repository.config.js';
import type { ContentUnit } from '../content/types.js';
import {
  NetworkError,
  ServerError,
  TranslationLockedError,
  TranslationNotFoundError,
} from '../errors/translation-errors.js';
import type { IAsyncRepository } from '../interfaces/repository.interface.js';
import type {
  ContentUnitData,
  TranslationData,
} from '../types/translation-data.types.js';
import { logger } from '../utils/logger.js';
import { describeContent } from '../utils/log-safe.js';

/**
 * Surface self-identification (hub Track B7). Snippet, WordPress and CLI all
 * share one project-wide (NULL-binding) pk_, so the server's surface registry
 * can't tell them apart from the key alone. Core is the snippet/SDK surface, so
 * every request advertises it via this header. Additive + inert: an older
 * server ignores the unknown header, so this ships safely in any order relative
 * to the server side. (Edge and webhook are out of scope — their bound keys
 * already identify them; a redundant tag would be a second source of truth.)
 */
const SURFACE_HEADER = 'X-LR-Surface';
const SURFACE_TAG = 'snippet';

/** Server version + the exact payload it describes, stored atomically. */
interface NamespaceSnapshot {
  version: string;
  translations: Record<string, unknown>;
}

/**
 * API repository implementation
 * Pure data access - no caching, no business logic
 */
export class NetworkRepository
  implements IAsyncRepository<NetworkRepositoryConfig>
{
  readonly name = 'NetworkRepository';
  readonly type = 'network';
  readonly config: NetworkRepositoryConfig = {};

  /**
   * Namespace snapshot per key "locale:namespace": the server version TOGETHER
   * with the payload it describes, persisted as ONE localStorage entry.
   *
   * Canonical 304 contract (same as edge KV bundle + WP transient cache):
   * never claim freshness you can't back. If-None-Match is only sent when the
   * snapshot is in hand; a 304 answers with that snapshot's payload; version
   * and payload can never desync because they live and die together.
   */
  private namespaceSnapshots = new Map<string, NamespaceSnapshot>();

  /** localStorage key prefix for persisted snapshots (version + payload) */
  private static readonly SNAPSHOT_PREFIX = 'lionrapid:ns:';

  /** Legacy bare-version prefix — a version WITHOUT its payload is exactly the
   *  desync the snapshot store exists to prevent; entries are deleted on sight. */
  private static readonly LEGACY_VERSION_PREFIX = 'lionrapid:v:';

  /**
   * Miss-path flood protection (READINESS before-scale #1: one cold locale
   * switch measured ~380 requests in 10 s — every t() miss refired a key GET
   * and a namespace GET on every re-render, forever on animated pages).
   */

  /** GETs currently on the wire, keyed by `url|if-none-match` — identical
   *  concurrent requests share one fetch; each awaiter gets a clone. The
   *  If-None-Match value is part of the key so the 304-without-snapshot
   *  unconditional refetch never coalesces onto a conditional flight. */
  private inflightGets = new Map<string, Promise<Response>>();

  /** URL → 404/423 seen recently: repeats are answered locally (same typed
   *  error, no network) until `until`. 423 honors the server's Retry-After.
   *  Cleared entirely by any successful write — discovery sync keeps its
   *  miss → batch → served-back round-trip. */
  private negativeCache = new Map<string, { status: number; until: number }>();

  /** One-way latch: a 401 anywhere (or a 403 on a READ) means the key is
   *  wrong or unauthorized for this project, and retrying cannot heal it —
   *  after the first one nothing touches the network again for this
   *  instance (a page reload starts fresh). Holds the status that tripped
   *  it so short-circuits answer honestly. */
  private authFailedStatus: number | null = null;

  /** Write-only latch (auth-byok Phase 18): a 403 (scope) or 429 (quota) on
   *  a WRITE degrades this instance to read-only — writes stop, silently
   *  and without retries, while reads keep serving the page. */
  private writesFailedStatus: number | null = null;

  private static readonly DEFAULT_NEGATIVE_TTL_MS = 60_000;

  /**
   * Default endpoint templates with placeholder support
   * Placeholders: :key, :locale, :namespace
   */
  private readonly DEFAULT_ENDPOINTS = {
    getTranslation: '/api/integrations/translations/:key',
    getNamespace: '/api/integrations/translations/:locale/:namespace',
    getProjectConfig: '/api/integrations/project-config',
    setTranslation: '/api/integrations/sync/:key',
    setNamespace: '/api/integrations/sync/:locale/:namespace',
    setBatch: '/api/integrations/sync/batch',
    removeTranslation: '/api/integrations/sync/:key',
    clearTranslations: '/translations',
  } as const;

  constructor(config: NetworkRepositoryConfig = {}) {
    this.config = {
      options: {
        timeout: config.options?.timeout ?? 5000,
        baseUrl: config.options?.baseUrl ?? '',
        headers: config.options?.headers ?? {},
      },
      ...config,
    };
  }

  /**
   * Get single translation from API
   * Uses platform-specific integration endpoint
   * @throws {TranslationNotFoundError} When translation doesn't exist (404)
   * @throws {TranslationLockedError} When translation is locked/not ready (423)
   * @throws {ServerError} When server returns 5xx error
   * @throws {NetworkError} When network request fails
   */
  async get(key: string): Promise<string> {
    if (!this.config?.options?.baseUrl) {
      logger.debug('API base URL is not set');
      throw new NetworkError('API base URL is not set');
    }

    const url = this.buildUrl('getTranslation', { key });
    logger.debug(`API GET: ${url}`);

    try {
      const response = await this.makeRequest(url, 'GET');

      if (!response.ok) {
        this.handleHttpError(response, key);
      }

      const data = await response.json();
      logger.debug('API response data:', data);

      // Try multiple key formats: meta.key, original key, or first translation
      const translation = data.translations[data.meta?.key]
        || data.translations[key]
        || Object.values(data.translations)[0];

      logger.debug(`Fetched translation for key "${key}":`, translation);
      if (!translation) {
        throw new TranslationNotFoundError(key);
      }

      // Handle both ContentUnit format and plain string
      if (typeof translation === 'object' && 'text' in translation) {
        logger.debug(`ContentUnit text: ${describeContent(translation.text)}`);
        return translation.text;
      }

      return String(translation);
    } catch (error) {
      this.handleCaughtError(error);
    }
  }

  /**
   * Get single translation as full ContentUnit (with styles)
   * Used by NetworkHandler for individual key lookups that need styles
   * @throws {TranslationNotFoundError} When translation doesn't exist (404)
   * @throws {TranslationLockedError} When translation is locked/not ready (423)
   * @throws {ServerError} When server returns 5xx error
   * @throws {NetworkError} When network request fails
   */
  async getContentUnit(key: string): Promise<ContentUnit> {
    if (!this.config?.options?.baseUrl) {
      logger.debug('API base URL is not set');
      throw new NetworkError('API base URL is not set');
    }

    const url = this.buildUrl('getTranslation', { key });
    logger.debug(`API GET ContentUnit: ${url}`);

    try {
      const response = await this.makeRequest(url, 'GET');

      if (!response.ok) {
        this.handleHttpError(response, key);
      }

      const data = await response.json();
      logger.debug('API response data:', data);

      // Try multiple key formats: meta.key, original key, or first translation
      const translation = data.translations[data.meta?.key]
        || data.translations[key]
        || Object.values(data.translations)[0];

      if (!translation) {
        throw new TranslationNotFoundError(key);
      }

      // Handle both ContentUnit format and plain string
      if (typeof translation === 'object' && 'text' in translation) {
        // Server returned ContentUnit format
        logger.debug(
          `ContentUnit for "${key}": text=${describeContent(translation.text)}, styles=`,
          translation.styles
        );
        return {
          text: translation.text,
          styles: translation.styles || {},
        };
      }

      // Server returned plain string - wrap in ContentUnit
      logger.debug(
        `Plain string for "${key}": ${describeContent(String(translation))} (no styles)`
      );
      return {
        text: String(translation),
        styles: {},
      };
    } catch (error) {
      this.handleCaughtError(error);
    }
  }

  /**
   * Get namespace with all translations
   * @throws {TranslationNotFoundError} When namespace doesn't exist (404)
   * @throws {TranslationLockedError} When namespace is locked/not ready (423)
   * @throws {ServerError} When server returns 5xx error
   * @throws {NetworkError} When network request fails
   */
  async getNamespace(
    locale: string,
    namespace: string
  ): Promise<TranslationData | null> {
    if (!this.config?.options?.baseUrl) {
      logger.debug('API base URL is not set');
      throw new NetworkError('API base URL is not set');
    }

    try {
      return (await this.fetchNamespaceRaw(locale, namespace)) as TranslationData;
    } catch (error) {
      this.handleCaughtError(error);
    }
  }

  /**
   * Project-level config for this key — the dashboard's language list,
   * served under the same trust model as translation reads (docs/20,
   * companion track). Returns null when the server doesn't expose the
   * endpoint (404 — older server, feature not deployed) or the payload is
   * unusable: absence means "keep your local fallback", never an error.
   */
  async getProjectConfig(): Promise<ProjectConfig | null> {
    if (!this.config?.options?.baseUrl) {
      throw new NetworkError('API base URL is not set');
    }

    const url = this.buildUrl('getProjectConfig', {});
    logger.debug(`API GET ProjectConfig: ${url}`);

    try {
      const response = await this.makeRequest(url, 'GET');

      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        this.handleHttpError(response, 'project-config');
      }

      const data = (await response.json()) as Partial<ProjectConfig> | null;
      if (
        !data ||
        typeof data.defaultLocale !== 'string' ||
        !Array.isArray(data.languages)
      ) {
        logger.warn('project-config payload unusable — keeping local fallback');
        return null;
      }

      return {
        defaultLocale: data.defaultLocale,
        languages: data.languages.map(String),
      };
    } catch (error) {
      this.handleCaughtError(error);
    }
  }

  /**
   * Shared namespace fetch with the canonical 304 contract:
   * - If-None-Match is sent ONLY when a snapshot (version + payload) is in hand.
   * - 304 answers with that snapshot's payload — never with "nothing".
   * - A 304 with no snapshot (server/proxy surprise) refetches unconditionally.
   * - A 200 stores version + payload atomically before either is trusted.
   */
  private async fetchNamespaceRaw(
    locale: string,
    namespace: string,
    allowConditional = true
  ): Promise<Record<string, unknown>> {
    const url = this.buildUrl('getNamespace', { locale, namespace });
    logger.debug(`API GET Namespace: ${url}`);

    const namespaceKey = `${locale}:${namespace}`;

    const snapshot = allowConditional ? this.getSnapshot(namespaceKey) : undefined;
    const extraHeaders: Record<string, string> = {};
    if (snapshot) {
      extraHeaders['If-None-Match'] = `W/"${snapshot.version}"`;
    }

    const response = await this.makeRequest(url, 'GET', undefined, extraHeaders);

    if (response.status === 304) {
      if (snapshot) {
        // Not modified — serve the exact payload the version vouches for, so
        // callers rehydrate the cache layers instead of assuming they're intact.
        logger.debug(`Namespace ${namespaceKey}: 304 Not Modified (snapshot served)`);
        return snapshot.translations;
      }
      // We never claimed freshness — refetch without the conditional header.
      logger.debug(`Namespace ${namespaceKey}: unexpected 304 with no snapshot — refetching`);
      return this.fetchNamespaceRaw(locale, namespace, false);
    }

    if (!response.ok) {
      this.handleHttpError(response, namespaceKey);
    }

    const data = await response.json();
    logger.debug('API response data:', data);

    if (!data.translations) {
      throw new TranslationNotFoundError(namespaceKey);
    }

    const version = data.meta?.version != null ? String(data.meta.version) : null;
    if (version) {
      this.setSnapshot(namespaceKey, { version, translations: data.translations });
    }

    return data.translations;
  }

  /**
   * Save translation to API (if supported)
   */
  async set(key: string, value: string): Promise<void> {
    if (!this.config?.options?.baseUrl) {
      logger.debug('API base URL is not set');
      return Promise.reject(new Error('API base URL is not set'));
    }
    try {
      const url = this.buildUrl('setTranslation', { key });
      const response = await this.makeRequest(url, 'PUT', {
        translation: value,
      });

      if (!response.ok) {
        throw new Error(`Failed to save translation: ${response.status}`);
      }
    } catch (error) {
      logger.debug(`API set failed for key "${key}":`, error);
      throw error;
    }
  }

  async setNamespace(
    locale: string,
    namespace: string,
    translations: TranslationData
  ): Promise<void> {
    if (!this.config?.options?.baseUrl) {
      logger.debug('API base URL is not set');
      return Promise.reject(new Error('API base URL is not set'));
    }
    try {
      const url = this.buildUrl('setNamespace', { locale, namespace });
      const response = await this.makeRequest(url, 'PUT', {
        translations,
      });

      if (!response.ok) {
        throw new Error(`Failed to save namespace: ${response.status}`);
      }
    } catch (error) {
      logger.debug(
        `API setNamespace failed for ${locale}:${namespace}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Batch sync multiple missing translations
   * Uses new batch endpoint for efficiency
   */
  async setBatch(items: Array<{ key: string; value: string }>): Promise<void> {
    if (!this.config?.options?.baseUrl) {
      logger.debug('API base URL is not set');
      return Promise.reject(new Error('API base URL is not set'));
    }

    try {
      const url = this.buildUrl('setBatch');
      logger.debug(`API POST Batch: ${url} (${items.length} items)`);

      const response = await this.makeRequest(url, 'POST', {
        translations: items,
      });

      if (!response.ok) {
        throw new Error(`Failed to sync batch: ${response.status}`);
      }

      logger.debug(`Batch sync successful: ${items.length} items`);
    } catch (error) {
      logger.debug(`API setBatch failed for ${items.length} items:`, error);
      throw error;
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // ContentUnit API Methods
  // ════════════════════════════════════════════════════════════════════════════

  /**
   * Get namespace with all translations as ContentUnit data
   *
   * @returns ContentUnitData (Record<string, ContentUnit>)
   * @throws {TranslationNotFoundError} When namespace doesn't exist (404)
   * @throws {TranslationLockedError} When namespace is locked/not ready (423)
   * @throws {ServerError} When server returns 5xx error
   * @throws {NetworkError} When network request fails
   */
  async getNamespaceContentUnits(
    locale: string,
    namespace: string
  ): Promise<ContentUnitData | null> {
    if (!this.config?.options?.baseUrl) {
      logger.debug('API base URL is not set');
      throw new NetworkError('API base URL is not set');
    }

    try {
      // Server always returns ContentUnit format
      return (await this.fetchNamespaceRaw(locale, namespace)) as ContentUnitData;
    } catch (error) {
      this.handleCaughtError(error);
    }
  }

  /**
   * Save ContentUnit to API
   *
   * Sends the ContentUnit format to server for storage.
   * Server should store in ContentUnit format for efficient retrieval.
   *
   * @param key - Translation key
   * @param unit - ContentUnit to save
   */
  async setContentUnit(key: string, unit: ContentUnit): Promise<void> {
    if (!this.config?.options?.baseUrl) {
      logger.debug('API base URL is not set');
      return Promise.reject(new Error('API base URL is not set'));
    }

    try {
      const url = this.buildUrl('setTranslation', { key });
      const response = await this.makeRequest(url, 'PUT', {
        translation: unit,
        format: 'contentunit', // Signal to server that this is ContentUnit format
      });

      if (!response.ok) {
        throw new Error(`Failed to save ContentUnit: ${response.status}`);
      }

      logger.debug(`Saved ContentUnit for key "${key}"`);
    } catch (error) {
      logger.debug(`API setContentUnit failed for key "${key}":`, error);
      throw error;
    }
  }

  /**
   * Batch sync multiple ContentUnits
   *
   * Sends ContentUnit format to server for batch storage.
   *
   * @param items - Array of key-ContentUnit pairs
   */
  async setBatchContentUnits(
    items: Array<{ key: string; value: ContentUnit }>
  ): Promise<void> {
    if (!this.config?.options?.baseUrl) {
      logger.debug('API base URL is not set');
      return Promise.reject(new Error('API base URL is not set'));
    }

    try {
      const url = this.buildUrl('setBatch');
      logger.debug(
        `API POST Batch ContentUnits: ${url} (${items.length} items)`
      );

      const response = await this.makeRequest(url, 'POST', {
        translations: items,
        format: 'contentunit', // Signal to server that this is ContentUnit format
      });

      if (!response.ok) {
        throw new Error(`Failed to sync batch ContentUnits: ${response.status}`);
      }

      logger.debug(`Batch ContentUnit sync successful: ${items.length} items`);
    } catch (error) {
      logger.debug(
        `API setBatchContentUnits failed for ${items.length} items:`,
        error
      );
      throw error;
    }
  }

  /**
   * Remove translation from API (if supported)
   */
  async remove(key: string): Promise<void> {
    if (!this.config?.options?.baseUrl) {
      logger.debug('API base URL is not set');
      return Promise.reject(new Error('API base URL is not set'));
    }
    try {
      const url = this.buildUrl('removeTranslation', { key });
      const response = await this.makeRequest(url, 'DELETE');

      if (!response.ok) {
        throw new Error(`Failed to remove translation: ${response.status}`);
      }
    } catch (error) {
      logger.debug(`API remove failed for key "${key}":`, error);
      throw error;
    }
  }

  /**
   * Clear all translations (if supported)
   */
  async clear(): Promise<void> {
    if (!this.config?.options?.baseUrl) {
      logger.debug('API base URL is not set');
      return Promise.reject(new Error('API base URL is not set'));
    }
    try {
      const url = this.buildUrl('clearTranslations');
      const response = await this.makeRequest(url, 'DELETE');

      if (!response.ok) {
        throw new Error(`Failed to clear translations: ${response.status}`);
      }
    } catch (error) {
      logger.debug('API clear failed:', error);
      throw error;
    }
  }

  getConfig(): NetworkRepositoryConfig {
    return this.config;
  }

  /** Read a persisted snapshot (version + payload), falling back to memory.
   *  Legacy bare-version entries and corrupt snapshots are deleted on sight —
   *  a version without its payload must never drive a conditional request. */
  private getSnapshot(namespaceKey: string): NamespaceSnapshot | undefined {
    const mem = this.namespaceSnapshots.get(namespaceKey);
    if (mem) return mem;
    try {
      localStorage.removeItem(NetworkRepository.LEGACY_VERSION_PREFIX + namespaceKey);
      const raw = localStorage.getItem(NetworkRepository.SNAPSHOT_PREFIX + namespaceKey);
      if (raw) {
        const parsed = JSON.parse(raw) as NamespaceSnapshot;
        if (
          parsed &&
          typeof parsed.version === 'string' &&
          parsed.version.length > 0 &&
          parsed.translations &&
          typeof parsed.translations === 'object'
        ) {
          this.namespaceSnapshots.set(namespaceKey, parsed);
          return parsed;
        }
        localStorage.removeItem(NetworkRepository.SNAPSHOT_PREFIX + namespaceKey);
      }
    } catch {
      /* SSR, localStorage unavailable, or corrupt JSON */
    }
    return undefined;
  }

  /** Persist version + payload as ONE entry (memory + localStorage). On quota
   *  failure the previous stored snapshot stays — still a consistent pair. */
  private setSnapshot(namespaceKey: string, snapshot: NamespaceSnapshot): void {
    this.namespaceSnapshots.set(namespaceKey, snapshot);
    try {
      localStorage.setItem(
        NetworkRepository.SNAPSHOT_PREFIX + namespaceKey,
        JSON.stringify(snapshot)
      );
      localStorage.removeItem(NetworkRepository.LEGACY_VERSION_PREFIX + namespaceKey);
    } catch {
      /* quota exceeded or unavailable — memory copy still serves this session */
    }
  }

  /**
   * Handle HTTP error responses by mapping to domain errors
   * @throws {TranslationNotFoundError} When status is 404
   * @throws {TranslationLockedError} When status is 423
   * @throws {ServerError} When status is 5xx
   * @throws {NetworkError} For other client errors
   */
  private handleHttpError(response: Response, key: string): never {
    logger.debug(`API response: ${response.status} ${response.statusText}`);

    if (response.status === 404) {
      throw new TranslationNotFoundError(key);
    }

    if (response.status === 423) {
      const retryAfter = response.headers.get('Retry-After');
      throw new TranslationLockedError(
        key,
        retryAfter ? parseInt(retryAfter, 10) * 1000 : undefined
      );
    }

    if (response.status >= 500 && response.status < 600) {
      throw new ServerError(key, response.status);
    }

    // Other client errors (400, 401, 403, etc.)
    throw new NetworkError(`HTTP ${response.status}: ${response.statusText}`);
  }

  /**
   * Handle caught errors by wrapping non-domain errors
   * @throws {TranslationError} Always throws - propagates domain errors or wraps unexpected ones
   */
  private handleCaughtError(error: unknown): never {
    // Custom errors propagate automatically, wrap everything else
    if (
      error instanceof TranslationNotFoundError ||
      error instanceof TranslationLockedError ||
      error instanceof ServerError ||
      error instanceof NetworkError
    ) {
      throw error;
    }

    // Wrap unexpected errors (fetch abort, JSON parse, etc.)
    throw new NetworkError('Network request failed', error);
  }

  /**
   * Build URL from endpoint template and parameters
   * Supports placeholders: :key, :locale, :namespace
   * @param endpoint - Endpoint key from DEFAULT_ENDPOINTS
   * @param params - Parameters to replace placeholders with
   * @returns Complete URL with baseUrl + endpoint path
   */
  private buildUrl(
    endpoint: keyof typeof this.DEFAULT_ENDPOINTS,
    params: Record<string, string> = {}
  ): string {
    const baseUrl = this.config.options?.baseUrl || '';

    // Get endpoint template from config or use default
    let endpointTemplate =
      this.config.options?.endpoints?.[endpoint] ||
      this.DEFAULT_ENDPOINTS[endpoint];

    // Ensure endpoint starts with / for consistent URL building
    if (!endpointTemplate.startsWith('/')) {
      endpointTemplate = `/${endpointTemplate}`;
    }

    // Replace all placeholders with actual values (encoded)
    // Use global replace to handle multiple occurrences
    let path = endpointTemplate;
    for (const [key, value] of Object.entries(params)) {
      const placeholder = new RegExp(`:${key}`, 'g');
      path = path.replace(placeholder, encodeURIComponent(value));
    }

    return `${baseUrl}${path}`;
  }

  /**
   * Make HTTP request with timeout and headers
   */
  private makeRequest(
    url: string,
    method: string,
    body?: unknown,
    extraHeaders?: Record<string, string>
  ): Promise<Response> {
    if (this.authFailedStatus !== null) {
      return Promise.resolve(
        new Response(null, {
          status: this.authFailedStatus,
          statusText: `auth latched off after ${this.authFailedStatus}`,
        })
      );
    }

    if (method !== 'GET') {
      if (this.writesFailedStatus !== null) {
        return Promise.resolve(
          new Response(null, {
            status: this.writesFailedStatus,
            statusText: `writes latched off after ${this.writesFailedStatus}`,
          })
        );
      }
      return this.doFetch(url, method, body, extraHeaders).then(resp =>
        this.recordOutcome(url, method, resp)
      );
    }

    const negative = this.negativeCache.get(url);
    if (negative) {
      if (Date.now() < negative.until) {
        logger.debug(`Negative-cache hit (${negative.status}): ${url}`);
        const headers =
          negative.status === 423
            ? { 'Retry-After': String(Math.ceil((negative.until - Date.now()) / 1000)) }
            : undefined;
        return Promise.resolve(
          new Response(null, { status: negative.status, statusText: 'negative cache', headers })
        );
      }
      this.negativeCache.delete(url);
    }

    const flightKey = `${url}|${extraHeaders?.['If-None-Match'] ?? ''}`;
    const existing = this.inflightGets.get(flightKey);
    if (existing) {
      return existing.then(resp => this.cloneResponse(resp));
    }

    const flight = this.doFetch(url, method, body, extraHeaders)
      .then(resp => this.recordOutcome(url, method, resp))
      .finally(() => this.inflightGets.delete(flightKey));
    this.inflightGets.set(flightKey, flight);
    return flight.then(resp => this.cloneResponse(resp));
  }

  /** Bookkeeping on every real (non-synthesized) response — runs once per
   *  physical fetch, never once per coalesced awaiter. */
  private recordOutcome(url: string, method: string, response: Response): Response {
    const status = response.status;

    if (status === 401 || (status === 403 && method === 'GET')) {
      if (this.authFailedStatus === null) {
        this.authFailedStatus = status;
        // eslint-disable-next-line no-console
        console.warn(
          `[LionRapid] API key rejected (${status}) — network translations ` +
            'are disabled for this session. Fix the key and reload; ' +
            'retrying cannot heal an auth failure.'
        );
      }
      return response;
    }

    // A 403 (scope) or 429 (quota) on a WRITE degrades to read-only,
    // silently: the key still reads fine — only the sync path is denied,
    // and retrying won't change the server's answer (Phase 18).
    if ((status === 403 || status === 429) && method !== 'GET') {
      if (this.writesFailedStatus === null) {
        this.writesFailedStatus = status;
        logger.warn(
          `Write path disabled after ${status} (scope/quota) — running read-only`
        );
      }
      return response;
    }

    if (method === 'GET' && (status === 404 || status === 423)) {
      const retryAfterSec =
        status === 423 ? Number(response.headers?.get?.('Retry-After')) : NaN;
      const ttl =
        Number.isFinite(retryAfterSec) && retryAfterSec > 0
          ? retryAfterSec * 1000
          : (this.config.options?.negativeCacheTtlMs ??
            NetworkRepository.DEFAULT_NEGATIVE_TTL_MS);
      this.negativeCache.set(url, { status, until: Date.now() + ttl });
    } else if (method !== 'GET' && response.ok) {
      // A write landed — server state changed, cached misses may now exist.
      this.negativeCache.clear();
    } else if (method === 'GET' && response.ok) {
      this.negativeCache.delete(url);
    }

    return response;
  }

  /** Test doubles (and exotic runtimes) hand us plain objects — only real
   *  Responses need cloning so each coalesced awaiter can read the body. */
  private cloneResponse(response: Response): Response {
    return typeof response.clone === 'function' ? response.clone() : response;
  }

  private async doFetch(
    url: string,
    method: string,
    body?: unknown,
    extraHeaders?: Record<string, string>
  ): Promise<Response> {
    const controller = new AbortController();
    const timeout = this.config.options?.timeout ?? 10000;
    const timeoutId = setTimeout(
      () => controller.abort(),
      timeout
    );

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        [SURFACE_HEADER]: SURFACE_TAG,
        ...this.config.options?.headers,
        ...extraHeaders,
      };

      if (this.config.options?.apiKey) {
        headers['Authorization'] = `Bearer ${this.config.options.apiKey}`;
      }

      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
